// src/features/occupancy-report/components/occupancy/DateRangePickerComponent.tsx

import React, { useState, useEffect, useRef } from "react";
import { Control, UseFormSetValue, useWatch } from "react-hook-form";
import { Form } from "react-bootstrap";
import { format, subMonths } from "date-fns";
import { DateRangePicker, DateRange, RangeKeyDict } from 'react-date-range';
import 'react-date-range/dist/styles.css'; // main style file
import 'react-date-range/dist/theme/default.css'; // theme css file
import { useMediaQuery } from "../../hooks/useMediaQuery";

interface DateRangePickerComponentProps {
    control: Control<any>;
    setValue: UseFormSetValue<any>;
    errors: any; // Disimpan untuk potensi validasi di level skema
}

const DateRangePickerComponent: React.FC<DateRangePickerComponentProps> = ({ control, setValue }) => {
    const isMobile = useMediaQuery('(max-width: 768px)');
    const calendarRef = useRef<HTMLDivElement>(null);

    // State untuk visibilitas picker dan rentang tanggalnya
    const [isCalendarOpen, setIsCalendarOpen] = useState(false);
    const [dateRange, setDateRange] = useState([
        {
            startDate: subMonths(new Date(), 1),
            endDate: new Date(),
            key: 'selection'
        }
    ]);

    // Mengamati nilai form state untuk ditampilkan di input
    const startDate = useWatch({ control, name: 'filter.start_date' });
    const endDate = useWatch({ control, name: 'filter.end_date' });

    // Atur nilai default form saat komponen pertama kali dirender
    useEffect(() => {
        const today = new Date();
        const firstDayOfLastMonth = subMonths(today, 1);
        // Hanya atur jika belum ada nilai
        if (!startDate && !endDate) {
            setValue("filter.start_date", format(firstDayOfLastMonth, "yyyy-MM-dd"));
            setValue("filter.end_date", format(today, "yyyy-MM-dd"));
        }
    }, [setValue, startDate, endDate]);

    // Sinkronkan state picker lokal jika nilai form berubah dari luar
    useEffect(() => {
        if (startDate && endDate) {
            setDateRange([{
                startDate: new Date(startDate),
                endDate: new Date(endDate),
                key: 'selection'
            }])
        }
    }, [startDate, endDate]);

    // Handler ketika rentang tanggal baru dipilih di picker
    const handleDateChange = (rangesByKey: RangeKeyDict) => {
        const { selection } = rangesByKey;
        if (selection.startDate && selection.endDate) {
            setDateRange([{
                startDate: selection.startDate,
                endDate: selection.endDate,
                key: selection.key || 'selection'
            }]);
            // Perbarui state react-hook-form
            setValue('filter.start_date', format(selection.startDate, 'yyyy-MM-dd'), { shouldValidate: true });
            setValue('filter.end_date', format(selection.endDate, 'yyyy-MM-dd'), { shouldValidate: true });
        }
    };

    // Efek untuk menutup kalender saat mengklik di luar
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (calendarRef.current && !calendarRef.current.contains(event.target as Node)) {
                setIsCalendarOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, [calendarRef]);

    const calendarContainerStyle: React.CSSProperties = {
        position: 'absolute',
        zIndex: 1000,
        top: '100%',
        marginTop: '8px',
        border: '1px solid #dfe2e5',
        borderRadius: '8px',
        boxShadow: '0 6px 15px rgba(0, 0, 0, 0.15)',
        right: 0,
    };

    if (isMobile) {
        calendarContainerStyle.left = '50%';
        calendarContainerStyle.transform = 'translateX(-50%)';
        calendarContainerStyle.right = 'auto';
    }

    return (
        <>
            <Form.Label>Select Range:</Form.Label>
            <div className="position-relative" ref={calendarRef}>
                <div className="input-group">
                    <Form.Control
                        type='text'
                        readOnly
                        onClick={() => setIsCalendarOpen(!isCalendarOpen)}
                        value={startDate && endDate ? `${format(new Date(startDate), 'dd MMM yyyy')} - ${format(new Date(endDate), 'dd MMM yyyy')}` : 'Select date range'}
                        placeholder="Select date range"
                    />
                    <span className="input-group-text" onClick={() => setIsCalendarOpen(!isCalendarOpen)} style={{ cursor: 'pointer' }}>
                        <svg xmlns="http://www.w3.org/2000/svg" className="icon icon-tabler icon-tabler-calendar-event" width="20" height="20" viewBox="0 0 24 24" strokeWidth="2" stroke="currentColor" fill="none" strokeLinecap="round" strokeLinejoin="round">
                            <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                            <path d="M4 5m0 2a2 2 0 0 1 2 -2h12a2 2 0 0 1 2 2v12a2 2 0 0 1 -2 2h-12a2 2 0 0 1 -2 -2z"></path>
                            <path d="M16 3l0 4"></path>
                            <path d="M8 3l0 4"></path>
                            <path d="M4 11l16 0"></path>
                            <path d="M8 15h2v2h-2z"></path>
                        </svg>
                    </span>
                </div>
                {isCalendarOpen && (
                    <div style={calendarContainerStyle}>
                        {isMobile ? (
                            <DateRange
                                onChange={handleDateChange}
                                moveRangeOnFirstSelection={false}
                                ranges={dateRange}
                                months={1}
                                direction="vertical"
                                className="shadow-sm"
                            />
                        ) : (
                            <DateRangePicker
                                onChange={handleDateChange}
                                moveRangeOnFirstSelection={false}
                                ranges={dateRange}
                                months={2}
                                direction="horizontal"
                                inputRanges={[]}
                                staticRanges={[]}
                            />
                        )}
                    </div>
                )}
            </div>
        </>
    );
};

export default DateRangePickerComponent;